
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

interface Artist {
  id: string;
  slug: string;
  name: string;
  avatar: string;
  city: string;
  lanes: string[];
  verified: boolean;
  hasNewRelease: boolean;
}

interface FeaturedCarouselProps {
  artists: Artist[];
}

export default function FeaturedCarousel({ artists }: FeaturedCarouselProps) {
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (artists.length === 0) return null;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Featured Artists</h2>
        <div className="flex gap-2">
          <button
            onClick={() => scroll('left')}
            className="p-2 text-gray-400 hover:text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <i className="ri-arrow-left-s-line text-lg"></i>
          </button>
          <button
            onClick={() => scroll('right')}
            className="p-2 text-gray-400 hover:text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <i className="ri-arrow-right-s-line text-lg"></i>
          </button>
        </div>
      </div>

      <div 
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {artists.map((artist) => (
          <div
            key={artist.id}
            className="flex-shrink-0 w-48 bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group"
            onClick={() => navigate(`/artists/${artist.slug}`)}
          >
            <div className="relative mb-3">
              <img
                src={artist.avatar}
                alt={artist.name}
                className="w-full h-32 object-cover rounded-lg"
              />
              {artist.verified && (
                <div className="absolute top-2 right-2 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-white text-xs"></i>
                </div>
              )}
              {artist.hasNewRelease && (
                <div className="absolute bottom-2 left-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    New release
                  </span>
                </div>
              )}
            </div>

            <div>
              <h3 className="font-medium text-gray-900 text-sm mb-1 line-clamp-1">
                {artist.name}
              </h3>
              <p className="text-xs text-gray-500 mb-2">{artist.city}</p>
              <div className="flex flex-wrap gap-1">
                {artist.lanes.slice(0, 2).map((lane) => (
                  <span
                    key={lane}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                  >
                    {lane}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
